import { useCallback, useEffect, useMemo, useState } from "react";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import { STEPPER_TYPE } from "@components/StepperTabs/StepperTabs.types";
import PageLayout from "@layouts/PageLayout";
import type { VesselNominationDetailsResponse } from "@services/vessel-nomination/types";
import type { Vessel } from "@services/vessel/types";
import { getNominationById } from "@services/vesselNominationApi";
import { getVesselByIdNoCompanyIdValidation } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import VesselDetails from "./components/header/VesselDetails";
import FirstRequestDetails from "./steps/FirstRequestDetails";
import type { VesselNominationDecision } from "./types";

export const QUERY_KEY = "nomination";
export const TEST_ID_BASE = "vessel-nomination-details-page";
export const LANG_BASE = "vessel-nomination.nomination-details";
const SERVICE = "bff-vessel";

export type VesselNominationDetailsOutletContext = {
  nominationDetails: VesselNominationDetailsResponse | undefined;
};

const nominationStatusTabs = [
  {
    path: paths.vesselNomination.paq,
    label: "PAQ",
    testId: `${TEST_ID_BASE}-paq-panel`,
  },
  {
    path: paths.vesselNomination.documents,
    label: "Documents",
    testId: `${TEST_ID_BASE}-documents-panel`,
  },
];

const VesselNominationDetails = () => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const activeStepIndex = nominationStatusTabs.findIndex((tab) =>
    location.pathname.endsWith(`/${tab.path}`),
  );
  const currentStepIndex = activeStepIndex === -1 ? 0 : activeStepIndex;
  const [localDecision, setLocalDecision] = useState<VesselNominationDecision | null>(null);

  const {
    data: nominationDetails,
    error,
    isLoading,
  } = useQuery<VesselNominationDetailsResponse>({
    queryKey: [QUERY_KEY, SERVICE, id],
    queryFn: () => getNominationById(id as string),
    enabled: !!id,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    setLocalDecision(null);
  }, [id]);

  const effectiveNominationDetails = useMemo(() => {
    if (!nominationDetails || !localDecision) {
      return nominationDetails;
    }

    return {
      ...nominationDetails,
      status: localDecision.decisionStatus,
      decisionNote: {
        noteText: localDecision.noteText,
        attachmentUrl: localDecision.attachmentUrl,
        documentName: localDecision.documentName,
      },
      nominationTask: nominationDetails.nominationTask
        ? {
            ...nominationDetails.nominationTask,
            status: "COMPLETED" as const,
          }
        : nominationDetails.nominationTask,
    };
  }, [localDecision, nominationDetails]);

  const handleDecisionComplete = useCallback((decision: VesselNominationDecision) => {
    setLocalDecision(decision);
  }, []);

  const vesselId = effectiveNominationDetails?.vesselId;

  const { data: vesselDetails, isLoading: detailsLoading } = useQuery<Vessel>({
    queryKey: ["vessel", SERVICE, vesselId],
    queryFn: () => getVesselByIdNoCompanyIdValidation(SERVICE, vesselId as string),
    enabled: !!vesselId,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const outletContext: VesselNominationDetailsOutletContext = {
    nominationDetails: effectiveNominationDetails,
  };
  // RTA is populated only after the PAQ has been added. First-request nominations
  // can have no `eta` in the details response, so its presence cannot identify them.
  const isFirstRequest = Boolean(effectiveNominationDetails && !effectiveNominationDetails.rta);

  if (isLoading || detailsLoading) {
    return <LoadingOverlay loading={isLoading || detailsLoading} />;
  }
  if (error) {
    return <ErrorHandler error={error} />;
  }
  return (
    <PageLayout dir={dir}>
      <VesselDetails
        testId={TEST_ID_BASE}
        nominationId={id as string}
        vesselDetails={vesselDetails}
        nominationDetails={effectiveNominationDetails}
        isFirstRequest={isFirstRequest}
        onDecisionComplete={handleDecisionComplete}
      />
      <div className="px-24 pt-8 pb-8">
        {isFirstRequest ? (
          <FirstRequestDetails nominationDetails={effectiveNominationDetails} />
        ) : (
          <StepperTabs
            testId={`${TEST_ID_BASE}-stepper-tabs`}
            currentStepIndex={currentStepIndex}
            stepperType={STEPPER_TYPE.HORIZONTAL}
            isTypeChangeable={false}
            onStepperTabChange={(index) => {
              const tab = nominationStatusTabs[index];

              if (!tab || index === currentStepIndex) return;

              navigate(tab.path, { replace: true, state: location.state });
            }}
          >
            {nominationStatusTabs.map((tab) => (
              <StepperTabPanel key={tab.path} label={tab.label}>
                <div className="min-w-full flex flex-col bg-white" data-testid={tab.testId}>
                  <Outlet context={outletContext} />
                </div>
              </StepperTabPanel>
            ))}
          </StepperTabs>
        )}
      </div>
    </PageLayout>
  );
};

export default VesselNominationDetails;
